import type { AuftragsStatus } from '@awf/shared';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { FinalizationSpinner } from '../ui/lade-animation/finalization-spinner.js';
import { RevisionSpinner } from '../ui/lade-animation/revision-spinner.js';
import { gruppeVon } from './status.js';
import type { SchichtArt } from './uebergabe.js';
import stile from './freigabe-page.module.css';

const TEXT: Record<SchichtArt, string> = {
  ueberarbeitung: 'Wird überarbeitet. Das kann bis zu 4 Minuten dauern.',
  freigabe: 'Wird freigegeben …',
  uebergabe: 'Wird übergeben …',
};
const TEXT_GESCHEITERT: Record<SchichtArt, string> = {
  ueberarbeitung: 'Die Überarbeitung ist fehlgeschlagen.',
  freigabe: 'Die Fertigstellung ist fehlgeschlagen.',
  // Unerreichbar: `uebergabe` gibt es nur bei `wartet_auf_freigabe`, nie in der Fehler-Gruppe.
  uebergabe: 'Die Fertigstellung ist fehlgeschlagen.',
};

/**
 * Steht ueber der ausgegrauten, `inert` geschalteten Arbeitsflaeche, solange die Runde nicht offen
 * ist (Spec 2026-09-29-absende-rennen Abschnitt 5.3). Ersetzt die Bestaetigung aus B19 (Befund 9): Die
 * Flaeche bleibt sichtbar, ist aber nicht bedienbar, und die Seite entsperrt sich von selbst,
 * sobald n8n die naechste Fassung vorlegt.
 */
export function UebergabeSchicht({
  art,
  status,
  runId,
  eigene,
}: {
  art: SchichtArt;
  status: AuftragsStatus;
  runId: string;
  /** Hat DIESER Tab abgeschickt? Dann leitet die Seite bei einer Freigabe selbst weiter. */
  eigene: boolean;
}) {
  const text = useRef<HTMLParagraphElement>(null);
  // Der Knopf, auf dem der Fokus lag, wird mit der Flaeche `inert` -- ohne dieses Fokussieren
  // fiele er auf `body`. Nur beim Einhaengen; spaetere Textwechsel meldet die Live-Region.
  useEffect(() => {
    text.current?.focus();
  }, []);

  const gescheitert = gruppeVon(status) === 'fehler';
  // `uebergabe` kommt nur aus Firestore, also nie aus dem eigenen Tab: dort ebenfalls der Link.
  const mitLink = gescheitert || ((art === 'freigabe' || art === 'uebergabe') && !eigene);

  return (
    <div className={stile.schicht}>
      <div className="eds-card">
        {/* Die bewegten Figuren der Content-Factory (VorgangsOverlay): beim Ueberarbeiten tippt
            jemand, beim Freigeben hakt ein Stift die Liste ab. Statt eines Kreisels. */}
        {!gescheitert && (
          <span className={stile.figur} data-testid="uebergabe-spinner">
            {art === 'ueberarbeitung' ? <RevisionSpinner /> : <FinalizationSpinner />}
          </span>
        )}
        {/* Live-Region nur die Statuszeile: auf der ganzen Karte saehe ein Screenreader bei jedem
            Wechsel auch den Link "Zum Auftrag" als Teil der Meldung. */}
        <p ref={text} tabIndex={-1} role="status" className="eds-card__title">
          {gescheitert ? TEXT_GESCHEITERT[art] : TEXT[art]}
        </p>
        {!gescheitert && art === 'ueberarbeitung' && (
          <p className={stile.schichtNebensatz}>Danach geht es hier von selbst weiter.</p>
        )}
        {mitLink && (
          <Link className="eds-button eds-button--primary" to={`/auftraege/${runId}`}>
            Zum Auftrag
          </Link>
        )}
      </div>
    </div>
  );
}
